import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Calculator, Loader2, ChevronDown, ChevronUp, Save, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import { LOGO_BASE64 } from "@/lib/logo";
import TagBarcodePicker from "@/components/TagBarcodePicker";
import PdfPromptStep from "@/components/PdfPromptStep";

// Empty "New WSP / New MRP" boxes injected into every PDF export for hand-written overrides
const NEW_WSP_MRP_BLOCK = `
<div style="display:flex;gap:16px;margin-top:14px;">
  <div style="flex:1;border:1px solid #E7E5E4;border-radius:6px;padding:10px 14px;">
    <div style="font-size:9px;color:#78716c;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">New WSP</div>
    <div style="height:22px;border-bottom:1px solid #ccc;"></div>
  </div>
  <div style="flex:1;border:1px solid #E7E5E4;border-radius:6px;padding:10px 14px;">
    <div style="font-size:9px;color:#78716c;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">New MRP</div>
    <div style="height:22px;border-bottom:1px solid #ccc;"></div>
  </div>
</div>`;

export default function CostingCalculatorPage() {
  const [yarnData, setYarnData] = useState({ counts: [], qualities: [], items: [] });
  const [borderTypes, setBorderTypes] = useState([]);
  const [processingTypes, setProcessingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [tagStepId, setTagStepId] = useState(null);
  const [pdfStep, setPdfStep] = useState(false);
  const [designName, setDesignName] = useState("");
  const [designDesc, setDesignDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadedDesignId, setLoadedDesignId] = useState(null);
  const [vendorNames, setVendorNames] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [wspMargin, setWspMargin] = useState("");
  const [wspPrice, setWspPrice] = useState("");
  const [mrpMargin, setMrpMargin] = useState("");
  const [mrpPrice, setMrpPrice] = useState("");
  const [wspMarkup, setWspMarkup] = useState("");
  const [wspMarkdown, setWspMarkdown] = useState("");
  const [mrpMarkup, setMrpMarkup] = useState("");
  const [mrpMarkdown, setMrpMarkdown] = useState("");
  const [revMrpInput, setRevMrpInput] = useState("");
  const [revWspMarkup, setRevWspMarkup] = useState("");
  const [revWspMarkdown, setRevWspMarkdown] = useState("");
  const [revWspResult, setRevWspResult] = useState("");
  const [pdfNameOpen, setPdfNameOpen] = useState(false);
  const [pdfDesignName, setPdfDesignName] = useState("");
  const [calcMode, setCalcMode] = useState("regular"); // "regular", "fob", or "shirt"

  // FOB state
  const [fobProductCost, setFobProductCost] = useState("");
  const [fobDiscount, setFobDiscount] = useState("");
  const [fobTrimsCost, setFobTrimsCost] = useState("");
  const [fobBorderType, setFobBorderType] = useState("");
  const [fobResult, setFobResult] = useState(null);
  const [fobWspMarkup, setFobWspMarkup] = useState("");
  const [fobWspPrice, setFobWspPrice] = useState("");
  const [fobMrpMarkup, setFobMrpMarkup] = useState("");
  const [fobMrpPrice, setFobMrpPrice] = useState("");
  const [fobDesignName, setFobDesignName] = useState("");
  const [fobDesignDesc, setFobDesignDesc] = useState("");
  const [fobSaveOpen, setFobSaveOpen] = useState(false);
  const [fobSaving, setFobSaving] = useState(false);
  const [fobWspMarkdown, setFobWspMarkdown] = useState("");
  const [fobMrpMarkdown, setFobMrpMarkdown] = useState("");
  const [fobRevMrp, setFobRevMrp] = useState("");
  const [fobRevMu, setFobRevMu] = useState("");
  const [fobRevMd, setFobRevMd] = useState("");
  const [fobRevWspResult, setFobRevWspResult] = useState("");

  // Shirt state
  const [shirtFabricRate, setShirtFabricRate] = useState("");
  const [shirtConsumption, setShirtConsumption] = useState("");
  const [shirtCmt, setShirtCmt] = useState("");
  const [shirtResult, setShirtResult] = useState(null);
  const [shirtWspMarkup, setShirtWspMarkup] = useState("");
  const [shirtWspPrice, setShirtWspPrice] = useState("");
  const [shirtMrpMarkup, setShirtMrpMarkup] = useState("");
  const [shirtMrpPrice, setShirtMrpPrice] = useState("");
  const [shirtWspMarkdown, setShirtWspMarkdown] = useState("");
  const [shirtMrpMarkdown, setShirtMrpMarkdown] = useState("");
  const [shirtRevMrp, setShirtRevMrp] = useState("");
  const [shirtRevMu, setShirtRevMu] = useState("");
  const [shirtRevMd, setShirtRevMd] = useState("");
  const [shirtRevWspResult, setShirtRevWspResult] = useState("");
  const [shirtDesignName, setShirtDesignName] = useState("");
  const [shirtDesignDesc, setShirtDesignDesc] = useState("");
  const [shirtSaveOpen, setShirtSaveOpen] = useState(false);
  const [shirtSaving, setShirtSaving] = useState(false);

  // Tissue state
  const [tissueWeft2Count, setTissueWeft2Count] = useState("");
  const [tissueWeft2Quality, setTissueWeft2Quality] = useState("");

  // Reset helpers — called after a successful save so user can immediately enter the next design
  const focusFirstField = (testId) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (el) el.focus();
    }, 50);
  };

  // After tagging a barcode, the backend auto-fills trims_cost / wsp / mrp and
  // recomputes landed_cost. Re-fetch and repopulate the in-memory state so the
  // subsequent PDF export uses the fresh numbers.
  const refreshAfterTag = async (designId, mode) => {
    if (!designId) return;
    try {
      const res = await api.get(`/saved-designs/${designId}`);
      const rd = res.data.result_data || {};
      const fd = res.data.form_data || {};
      const landed = Number(rd.landed_cost || rd.final_cost_per_dhoti || 0);

      const computeBands = (setMu, setMd, price, baseline) => {
        if (price && baseline > 0) {
          setMu((((price - baseline) / baseline) * 100).toFixed(2));
          setMd(price > 0 ? (((price - baseline) / price) * 100).toFixed(2) : "");
        }
      };

      if (mode === "fob") {
        setFobTrimsCost(fd.trims_cost != null ? String(fd.trims_cost) : "");
        setFobResult((prev) => ({ ...(prev || {}), ...rd }));
        if (rd.wsp_price) {
          setFobWspPrice(String(rd.wsp_price));
          computeBands(setFobWspMarkup, setFobWspMarkdown, Number(rd.wsp_price), landed);
        }
        if (rd.mrp_price) {
          setFobMrpPrice(String(rd.mrp_price));
          const wspBase = Number(rd.wsp_price) || landed;
          computeBands(setFobMrpMarkup, setFobMrpMarkdown, Number(rd.mrp_price), wspBase);
        }
      } else if (mode === "shirt") {
        setShirtResult((prev) => ({ ...(prev || {}), ...rd }));
        if (rd.wsp_price) {
          setShirtWspPrice(String(rd.wsp_price));
          computeBands(setShirtWspMarkup, setShirtWspMarkdown, Number(rd.wsp_price), landed);
        }
        if (rd.mrp_price) {
          setShirtMrpPrice(String(rd.mrp_price));
          const wspBase = Number(rd.wsp_price) || landed;
          computeBands(setShirtMrpMarkup, setShirtMrpMarkdown, Number(rd.mrp_price), wspBase);
        }
      } else {
        // regular & tissue
        if (fd.trims_cost != null) setForm((prev) => ({ ...prev, trims_cost: String(fd.trims_cost) }));
        setResult((prev) => ({ ...(prev || {}), ...rd }));
        if (rd.wsp_price) {
          setWspPrice(String(rd.wsp_price));
          computeBands(setWspMarkup, setWspMarkdown, Number(rd.wsp_price), landed);
        }
        if (rd.mrp_price) {
          setMrpPrice(String(rd.mrp_price));
          const wspBase = Number(rd.wsp_price) || landed;
          computeBands(setMrpMarkup, setMrpMarkdown, Number(rd.mrp_price), wspBase);
        }
      }
    } catch (err) { /* silent — user can still export with previous values */ }
  };
  const resetRegular = () => {
    setForm({
      fabric_width: "", border_width: "", reed: "", pick: "", pick_rate: "", size: "",
      border_type: "", bobbin_type: "both", processing_type: "",
      warp_count: "", warp_quality: "", weft_count: "", weft_quality: "",
      bobbin1_count: "", bobbin1_quality: "", bobbin2_count: "", bobbin2_quality: "",
      kuri_count: "", kuri_quality: "", trims_cost: "",
    });
    setResult(null);
    setDesignName(""); setDesignDesc(""); setLoadedDesignId(null); setVendorNames([]);
    setWspMargin(""); setWspPrice(""); setMrpMargin(""); setMrpPrice("");
    setWspMarkup(""); setWspMarkdown(""); setMrpMarkup(""); setMrpMarkdown("");
    setRevMrpInput(""); setRevWspMarkup(""); setRevWspMarkdown(""); setRevWspResult("");
    setTissueWeft2Count(""); setTissueWeft2Quality("");
    setShowDetails(false);
    setSearchParams({}, { replace: true });
    focusFirstField("fabric-width-input");
  };
  const resetFob = () => {
    setFobProductCost(""); setFobDiscount(""); setFobTrimsCost(""); setFobBorderType("");
    setFobResult(null);
    setFobDesignName(""); setFobDesignDesc("");
    setFobWspMarkup(""); setFobWspPrice(""); setFobMrpMarkup(""); setFobMrpPrice("");
    setFobWspMarkdown(""); setFobMrpMarkdown("");
    setFobRevMrp(""); setFobRevMu(""); setFobRevMd(""); setFobRevWspResult("");
    setLoadedDesignId(null); setVendorNames([]);
    setSearchParams({}, { replace: true });
    focusFirstField("fob-product-cost");
  };
  const resetShirt = () => {
    setShirtFabricRate(""); setShirtConsumption(""); setShirtCmt("");
    setShirtResult(null);
    setShirtDesignName(""); setShirtDesignDesc("");
    setShirtWspMarkup(""); setShirtWspPrice(""); setShirtMrpMarkup(""); setShirtMrpPrice("");
    setShirtWspMarkdown(""); setShirtMrpMarkdown("");
    setShirtRevMrp(""); setShirtRevMu(""); setShirtRevMd(""); setShirtRevWspResult("");
    setLoadedDesignId(null); setVendorNames([]);
    setSearchParams({}, { replace: true });
    focusFirstField("shirt-fabric-rate");
  };

  const handleExportPdf = () => {
    setPdfDesignName(designName || "");
    setPdfNameOpen(true);
  };

  const executePdfExport = () => {
    if (!result) return;
    setPdfNameOpen(false);
    const name = (pdfDesignName || designName || "").trim() || "Dhoti Costing";
    const r = result;
    const f = form;
    const s = r.size;

    const html = `
    <div style="font-family:'Work Sans','IBM Plex Sans',sans-serif;max-width:700px;padding:0;font-size:11px;color:#1C1917;">
      <!-- Logo & Title on white background -->
      <div style="text-align:center;margin-bottom:10px;padding:8px 0;">
        <img src="${LOGO_BASE64}" style="height:44px;margin:0 auto;" />
        <div style="font-size:11px;color:#78716c;margin-top:6px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">Dhoti Costing Sheet</div>
      </div>

      <!-- Header Banner -->
      <div style="background:linear-gradient(135deg,#C2410C,#7C2D12);color:white;border-radius:10px;padding:16px 20px;margin-bottom:12px;">
        <div style="font-size:14px;font-weight:700;text-align:center;margin-bottom:8px;">${name}</div>
        <div style="display:flex;justify-content:space-around;text-align:center;">
          <div><div style="font-size:9px;opacity:0.6;">Landed Cost</div><div style="font-size:20px;font-weight:700;">${r.landed_cost?.toFixed(2)}</div></div>
          <div><div style="font-size:9px;opacity:0.6;">WSP</div><div style="font-size:20px;font-weight:700;">${wspPrice || "—"}</div>${wspPrice ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${wspMarkup}% / MD ${wspMarkdown}%</div>` : ""}</div>
          <div><div style="font-size:9px;opacity:0.6;">MRP</div><div style="font-size:20px;font-weight:700;">${mrpPrice || "—"}</div>${mrpPrice ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${mrpMarkup}% / MD ${mrpMarkdown}%</div>` : ""}</div>
        </div>
      </div>

      <!-- Primary Specifications -->
      <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;margin-bottom:12px;">
        <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:8px;">Primary Specifications</div>
        <div style="display:flex;gap:24px;flex-wrap:wrap;">
          <div style="display:flex;gap:24px;">
            <div><span style="color:#78716c;">Fabric Width:</span> <b>${f.fabric_width}</b></div>
            <div><span style="color:#78716c;">Border Width:</span> <b>${f.border_width}</b></div>
            <div><span style="color:#78716c;">Loom Reed:</span> <b>${f.reed}</b></div>
            <div><span style="color:#78716c;">Loom Pick:</span> <b>${f.pick}</b></div>
            <div><span style="color:#78716c;">Fabric Reed:</span> <b>${parseFloat(f.reed) + 4}</b></div>
            <div><span style="color:#78716c;">Fabric Pick:</span> <b>${parseFloat(f.pick) - 2}</b></div>
            <div><span style="color:#78716c;">Pick Rate:</span> <b>${f.pick_rate}</b></div>
            <div><span style="color:#78716c;">Size:</span> <b>${f.size}m</b></div>
          </div>
          <div style="display:flex;gap:24px;">
            <div><span style="color:#78716c;">Border:</span> <b>${r.border_type}</b></div>
            <div><span style="color:#78716c;">Bobbin:</span> <b>${f.bobbin_type}</b></div>
            <div><span style="color:#78716c;">Processing:</span> <b>${r.processing_type || "—"}</b></div>
          </div>
          <div style="display:flex;gap:24px;">
            <div><span style="color:#78716c;">Warp:</span> <b>${r.warp_count}/${r.warp_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${r.warp_price_per_unit ?? "—"} + ${r.warp_sizing_per_unit ?? "—"}</span></div>
            <div><span style="color:#78716c;">Weft:</span> <b>${r.weft_count}/${r.weft_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${r.weft_price_per_unit ?? "—"}</span></div>
            <div><span style="color:#78716c;">Kuri:</span> <b>${r.kuri_count}/${r.kuri_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${r.kuri_price_per_unit ?? "—"}</span></div>
          </div>
          <div style="display:flex;gap:24px;">
            <div><span style="color:#78716c;">Reed Space:</span> <b>${r.reed_space}</b></div>
            <div><span style="color:#78716c;">Warp Ends:</span> <b>${r.warp_ends}</b></div>
            <div><span style="color:#78716c;">Warp Wt/m:</span> <b>${r.warp_weight_per_mtr?.toFixed(4)}</b></div>
            <div><span style="color:#78716c;">Weft Wt/m:</span> <b>${r.weft_weight_per_mtr?.toFixed(4)}</b></div>
          </div>
          ${vendorNames.length ? `<div style="width:100%;border-top:1px dashed #E7E5E4;padding-top:6px;margin-top:2px;"><span style="color:#78716c;">Vendors:</span> <b>${vendorNames.join(", ")}</b></div>` : ""}
        </div>
      </div>

      <!-- Two Column Layout -->
      <div style="display:flex;gap:12px;">
        <!-- LEFT: Cost Breakdown till Greige -->
        <div style="flex:1;background:white;border:1px solid #E7E5E4;border-radius:8px;padding:14px 16px;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:10px;">Cost Breakdown</div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            <tr style="border-bottom:1px solid #eee;font-size:9px;color:#78716c;font-weight:600;text-transform:uppercase;">
              <td style="padding:4px 0;"></td><td style="text-align:right;padding:4px 0;">Per Mtr</td><td style="text-align:right;padding:4px 0;">Per Dhoti</td>
            </tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Warp Raw Cost</td><td style="text-align:right;font-family:monospace;">${r.warp_raw_cost?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.warp_raw_cost*s).toFixed(2)}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Warp Sizing</td><td style="text-align:right;font-family:monospace;">${r.warp_sizing_charge?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.warp_sizing_charge*s).toFixed(2)}</td></tr>
            <tr style="border-bottom:1px solid #e7e5e4;font-weight:600;"><td style="padding:5px 0;">Warp Total</td><td style="text-align:right;font-family:monospace;">${r.warp_total_cost?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.warp_total_cost*s).toFixed(2)}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weft Cost</td><td style="text-align:right;font-family:monospace;">${r.weft_cost?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.weft_cost*s).toFixed(2)}</td></tr>
            ${r.has_bobbin1 ? `<tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Bobbin - Polyester</td><td style="text-align:right;font-family:monospace;">${r.bobbin1_cost?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.bobbin1_cost*s).toFixed(2)}</td></tr>` : ""}
            ${r.has_bobbin2 ? `<tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Bobbin - Jari</td><td style="text-align:right;font-family:monospace;">${r.bobbin2_cost?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.bobbin2_cost*s).toFixed(2)}</td></tr>` : ""}
            <tr style="border-bottom:1px solid #e7e5e4;font-weight:600;"><td style="padding:5px 0;">Total Bobbin</td><td style="text-align:right;font-family:monospace;">${r.total_bobbin_cost?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.total_bobbin_cost*s).toFixed(2)}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Kuri (${r.kuri_count}/${r.kuri_quality})</td><td style="text-align:right;font-family:monospace;">—</td><td style="text-align:right;font-family:monospace;">${r.kuri_cost?.toFixed(2)}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weaving Wages</td><td style="text-align:right;font-family:monospace;">${r.weaving_wages?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${(r.weaving_wages*s).toFixed(2)}</td></tr>
            <tr style="border-bottom:2px solid #C2410C;font-weight:700;"><td style="padding:6px 0;">Cost</td><td style="text-align:right;font-family:monospace;">${r.cost_per_mtr?.toFixed(2)}</td><td style="text-align:right;font-family:monospace;">${r.cost_per_dhoti?.toFixed(2)}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weaver Profit (${r.weaver_profit_pct}%)</td><td></td><td style="text-align:right;font-family:monospace;">${r.profit_amount?.toFixed(2)}</td></tr>
            <tr style="font-weight:700;font-size:12px;"><td style="padding:8px 0;color:#C2410C;">Greige Cost</td><td></td><td style="text-align:right;font-family:monospace;color:#C2410C;">${r.greige_cost_per_dhoti?.toFixed(2)}</td></tr>
          </table>
        </div>

        <!-- RIGHT: Processing, Warehouse, Final -->
        <div style="flex:1;display:flex;flex-direction:column;gap:12px;">
          <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:14px 16px;">
            <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:10px;">Post Production</div>
            <table style="width:100%;border-collapse:collapse;font-size:11px;">
              <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Processing <span style="color:#a8a29e;font-size:10px;">(${r.processing_mtr_rate}/mtr x ${s})</span></td><td style="text-align:right;font-family:monospace;">${r.processing_cost?.toFixed(2)}</td></tr>
              <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Folding</td><td style="text-align:right;font-family:monospace;">${r.folding_cost?.toFixed(2)}</td></tr>
              <tr style="border-bottom:1px solid #e7e5e4;"><td style="padding:5px 0;color:#57534E;">Trims</td><td style="text-align:right;font-family:monospace;">${r.trims_cost?.toFixed(2)}</td></tr>
              <tr style="border-bottom:2px solid #1C1917;font-weight:700;"><td style="padding:6px 0;">Final Cost</td><td style="text-align:right;font-family:monospace;">${r.final_cost_per_dhoti?.toFixed(2)}</td></tr>
              <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Warehouse (${r.warehouse_pct}%)</td><td style="text-align:right;font-family:monospace;">${r.warehouse_cost?.toFixed(2)}</td></tr>
              <tr style="font-weight:700;font-size:13px;color:#C2410C;"><td style="padding:8px 0;">Landed Cost</td><td style="text-align:right;font-family:monospace;">${r.landed_cost?.toFixed(2)}</td></tr>
            </table>
          </div>

          <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:14px 16px;">
            <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:10px;">Pricing</div>
            <table style="width:100%;border-collapse:collapse;font-size:11px;">
              ${wspPrice ? `
              <tr style="border-bottom:1px solid #f5f5f4;font-weight:600;"><td style="padding:5px 0;">WSP</td><td style="text-align:right;font-family:monospace;font-size:13px;">${wspPrice}</td></tr>
              <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:4px 0;color:#78716c;font-size:10px;">WSP Markup / Markdown</td><td style="text-align:right;color:#78716c;font-size:10px;">${wspMarkup}% / ${wspMarkdown}%</td></tr>
              ` : ""}
              ${mrpPrice ? `
              <tr style="border-bottom:1px solid #f5f5f4;font-weight:600;"><td style="padding:5px 0;">MRP</td><td style="text-align:right;font-family:monospace;font-size:13px;">${mrpPrice}</td></tr>
              <tr><td style="padding:4px 0;color:#78716c;font-size:10px;">MRP Markup / Markdown</td><td style="text-align:right;color:#78716c;font-size:10px;">${mrpMarkup}% / ${mrpMarkdown}%</td></tr>
              ` : ""}
              ${!wspPrice && !mrpPrice ? `<tr><td style="padding:5px 0;color:#a8a29e;" colspan="2">No pricing set</td></tr>` : ""}
            </table>
          </div>
        </div>
      </div>

      ${NEW_WSP_MRP_BLOCK}
      <div style="text-align:center;font-size:8px;color:#a8a29e;margin-top:10px;">Generated on ${new Date().toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"})}</div>
    </div>`;

    const container = document.createElement("div");
    container.innerHTML = html;
    document.body.appendChild(container);

    html2pdf().set({
      margin: [8, 8, 8, 8],
      filename: `Costing_${name}_${s}m_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.92 },
      html2canvas: { scale: 1.5, useCORS: true, logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    }).from(container).save().then(() => {
      document.body.removeChild(container);
    });
    toast.success("PDF exported!");
  };

  const [form, setForm] = useState({
    fabric_width: "",
    border_width: "",
    reed: "",
    pick: "",
    pick_rate: "",
    size: "",
    border_type: "",
    bobbin_type: "both",
    processing_type: "",
    warp_count: "",
    warp_quality: "",
    weft_count: "",
    weft_quality: "",
    bobbin1_count: "",
    bobbin1_quality: "",
    bobbin2_count: "",
    bobbin2_quality: "",
    kuri_count: "",
    kuri_quality: "",
    trims_cost: "",
  });

  const fetchDropdownData = useCallback(async () => {
    try {
      const [yarnRes, borderRes, procRes] = await Promise.all([
        api.get("/yarn-master/unique-values"),
        api.get("/border-master/types"),
        api.get("/processing-master/types"),
      ]);
      setYarnData(yarnRes.data);
      setBorderTypes(borderRes.data);
      setProcessingTypes(procRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);

  // Ctrl/Cmd+S → open Save dialog for the active tab (when a result is available)
  useEffect(() => {
    const handler = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S");
      if (!isSave) return;
      // Skip if any save dialog is already open or pdf dialog is open
      if (saveOpen || fobSaveOpen || shirtSaveOpen || pdfNameOpen) return;
      if (calcMode === "fob" && fobResult) {
        e.preventDefault();
        setFobSaveOpen(true);
      } else if (calcMode === "shirt" && shirtResult) {
        e.preventDefault();
        setShirtSaveOpen(true);
      } else if ((calcMode === "regular" || calcMode === "tissue") && result) {
        e.preventDefault();
        setSaveOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [calcMode, result, fobResult, shirtResult, saveOpen, fobSaveOpen, shirtSaveOpen, pdfNameOpen]);

  // Load design from URL param
  useEffect(() => {
    const designId = searchParams.get("load");
    const autoExport = searchParams.get("autoExport") === "1";
    if (designId && !loading) {
      api.get(`/saved-designs/${designId}`).then((res) => {
        const fd = res.data.form_data;
        const rd = res.data.result_data;
        const mode = fd?.calc_mode || rd?.calc_mode || "regular";
        setVendorNames(res.data.vendor_names || []);

        let exportTestId = "export-pdf-button"; // regular / tissue
        if (mode === "fob") {
          setCalcMode("fob");
          setFobProductCost(fd?.product_cost || "");
          setFobDiscount(fd?.discount_pct || "");
          setFobTrimsCost(fd?.trims_cost || "");
          setFobBorderType(fd?.border_type || "");
          if (rd) {
            // Recalculate FOB to ensure trims + discount are included in WH
            const pc = parseFloat(fd?.product_cost || rd?.product_cost || 0);
            const disc = parseFloat(fd?.discount_pct || rd?.discount_pct || 0);
            const tr = parseFloat(fd?.trims_cost || rd?.trims_cost || 0);
            const whPct = rd?.warehouse_pct || fobSettings.warehouse_pct || 5;
            const discAmt = pc * (disc / 100);
            const discProd = pc - discAmt;
            const costPerDhoti = discProd + tr;
            const whCost = costPerDhoti * (whPct / 100);
            const landed = costPerDhoti + whCost;
            setFobResult({
              product_cost: pc, discount_pct: disc, discount_amount: discAmt, discounted_product_cost: discProd,
              trims_cost: tr, cost_per_dhoti: costPerDhoti, warehouse_pct: whPct, warehouse_cost: whCost, landed_cost: landed,
            });
            if (rd.wsp_price) setFobWspPrice(String(rd.wsp_price));
            if (rd.mrp_price) setFobMrpPrice(String(rd.mrp_price));
          }
          setFobDesignName(res.data.design_name || "");
          setFobDesignDesc(res.data.description || "");
          setLoadedDesignId(designId);
          exportTestId = "fob-export-pdf";
          toast.success(`Loaded FOB design: ${res.data.design_name}`);
        } else if (mode === "shirt") {
          setCalcMode("shirt");
          setShirtFabricRate(fd?.fabric_rate || "");
          setShirtConsumption(fd?.consumption || "");
          setShirtCmt(fd?.cmt || "");
          if (rd) {
            setShirtResult(rd);
            if (rd.wsp_price) setShirtWspPrice(String(rd.wsp_price));
            if (rd.mrp_price) setShirtMrpPrice(String(rd.mrp_price));
          }
          setShirtDesignName(res.data.design_name || "");
          setShirtDesignDesc(res.data.description || "");
          setLoadedDesignId(designId);
          exportTestId = "shirt-export-pdf";
          toast.success(`Loaded Shirt design: ${res.data.design_name}`);
        } else if (mode === "tissue") {
          setCalcMode("tissue");
          if (fd) {
            setForm((prev) => ({ ...prev, ...fd }));
            setTissueWeft2Count(fd.weft2_count || "");
            setTissueWeft2Quality(fd.weft2_quality || "");
          }
          setDesignName(res.data.design_name || "");
          setDesignDesc(res.data.description || "");
          setLoadedDesignId(designId);
          setResult(rd || null);
          if (rd) setShowDetails(true);
          toast.success(`Loaded Tissue design: ${res.data.design_name}`);
        } else {
          // Regular
          setCalcMode("regular");
          if (fd) {
            setForm((prev) => ({ ...prev, ...fd }));
            setDesignName(res.data.design_name || "");
            setDesignDesc(res.data.description || "");
            setLoadedDesignId(designId);
            setResult(rd || null);
            if (rd) {
              setShowDetails(true);
              const landed = rd.landed_cost || rd.final_cost_per_dhoti || 0;
              if (rd.wsp_price) {
                const wsp = Number(rd.wsp_price);
                setWspPrice(String(wsp));
                setWspMarkup(landed > 0 ? (((wsp - landed) / landed) * 100).toFixed(2) : "");
                setWspMarkdown(wsp > 0 ? (((wsp - landed) / wsp) * 100).toFixed(2) : "");
                if (rd.mrp_price) {
                  const mrp = Number(rd.mrp_price);
                  setMrpPrice(String(mrp));
                  setMrpMarkup(wsp > 0 ? (((mrp - wsp) / wsp) * 100).toFixed(2) : "");
                  setMrpMarkdown(mrp > 0 ? (((mrp - wsp) / mrp) * 100).toFixed(2) : "");
                }
              }
            }
          }
          toast.success(`Loaded design: ${res.data.design_name}`);
        }
        setSearchParams({}, { replace: true });

        // If invoked from Saved Designs row → auto-click the mode-specific Export PDF button.
        // Delay so the result/state finishes rendering first, then return to /designs once
        // html2pdf has captured the DOM.
        if (autoExport) {
          setTimeout(() => {
            const btn = document.querySelector(`[data-testid="${exportTestId}"]`);
            if (btn) {
              btn.click();
              // html2pdf renders into a detached node and triggers a browser download — safe to navigate
              // away ~1.6s later (capture is well underway by then; download fires from native blob).
              setTimeout(() => navigate("/designs"), 1600);
            } else {
              toast.error("Could not auto-export — please click Export PDF on the page.");
            }
          }, 600);
        }
      }).catch(() => {});
    }
  }, [searchParams, loading, setSearchParams]);

  const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Filter qualities based on selected count
  const getQualitiesForCount = (count) => {
    if (!count) return yarnData.qualities;
    const items = yarnData.items.filter((i) => i.count === count);
    return [...new Set(items.map((i) => i.quality))].sort();
  };

  // Auto-fill quality if only one option for selected count
  const selectCountAndAutoFill = (countKey, qualityKey, count) => {
    updateForm(countKey, count);
    const quals = getQualitiesForCount(count);
    if (quals.length === 1) {
      updateForm(qualityKey, quals[0]);
    } else {
      updateForm(qualityKey, "");
    }
  };

  const handleSaveDesign = async () => {
    if (!designName.trim()) {
      toast.error("Please enter a design name");
      return;
    }
    setSaving(true);
    try {
      const landed = result?.landed_cost || 0;
      const wsp = wspPrice ? Number(wspPrice) : null;
      const mrp = mrpPrice ? Number(mrpPrice) : null;
      const payload = {
        design_name: designName,
        description: designDesc,
        form_data: { ...form, ...(calcMode === "tissue" ? { weft2_count: tissueWeft2Count, weft2_quality: tissueWeft2Quality, calc_mode: "tissue" } : { calc_mode: calcMode }) },
        result_data: {
          ...result,
          wsp_price: wsp,
          wsp_markup: wsp && landed ? (((wsp - landed) / landed) * 100).toFixed(2) : null,
          wsp_markdown: wsp && landed && wsp > 0 ? (((wsp - landed) / wsp) * 100).toFixed(2) : null,
          mrp_price: mrp,
          mrp_markup: mrp && wsp ? (((mrp - wsp) / wsp) * 100).toFixed(2) : null,
          mrp_markdown: mrp && wsp && mrp > 0 ? (((mrp - wsp) / mrp) * 100).toFixed(2) : null,
        },
      };
      if (loadedDesignId) {
        await api.put(`/saved-designs/${loadedDesignId}`, payload);
        toast.success("Design updated!");
        setPdfStep(true);
      } else {
        const res = await api.post("/saved-designs", payload);
        toast.success("Design saved!");
        setTagStepId(res.data.id);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };


  // FOB Calculator
  const [fobSettings, setFobSettings] = useState({ warehouse_pct: 5 });
  useEffect(() => {
    api.get("/settings").then((res) => {
      const s = {};
      (res.data || []).forEach((x) => { if (x.key === "warehouse_expenses") s.warehouse_pct = x.value; });
      setFobSettings(s);
    }).catch(() => {});
  }, []);

  const calculateFob = () => {
    const productCost = parseFloat(fobProductCost) || 0;
    const discountPct = parseFloat(fobDiscount) || 0;
    const trimsCost = parseFloat(fobTrimsCost) || 0;
    if (!productCost) { toast.error("Please enter Product Cost"); return; }
    const discountAmount = productCost * (discountPct / 100);
    const discountedProduct = productCost - discountAmount;
    const costPerDhoti = discountedProduct + trimsCost;
    const whPct = fobSettings.warehouse_pct || 5;
    const warehouseCost = costPerDhoti * (whPct / 100);
    const landedCost = costPerDhoti + warehouseCost;
    setFobResult({
      product_cost: productCost,
      discount_pct: discountPct,
      discount_amount: discountAmount,
      discounted_product_cost: discountedProduct,
      trims_cost: trimsCost,
      cost_per_dhoti: costPerDhoti,
      warehouse_pct: whPct,
      warehouse_cost: warehouseCost,
      landed_cost: landedCost,
    });
    // Preserve WSP/MRP — recompute margin % bands against new landed cost
    if (fobWspPrice && landedCost > 0) {
      const wsp = parseFloat(fobWspPrice);
      setFobWspMarkup((((wsp - landedCost) / landedCost) * 100).toFixed(2));
      setFobWspMarkdown(wsp > 0 ? (((wsp - landedCost) / wsp) * 100).toFixed(2) : "");
      if (fobMrpPrice) {
        const mrp = parseFloat(fobMrpPrice);
        setFobMrpMarkup(wsp > 0 ? (((mrp - wsp) / wsp) * 100).toFixed(2) : "");
        setFobMrpMarkdown(mrp > 0 ? (((mrp - wsp) / mrp) * 100).toFixed(2) : "");
      }
    } else if (!fobWspPrice) {
      setFobWspMarkup(""); setFobMrpMarkup("");
      setFobWspMarkdown(""); setFobMrpMarkdown("");
    }
    toast.success("FOB cost calculated!");
  };

  const onFobWspPriceChange = (val) => {
    setFobWspPrice(val);
    if (val && fobResult) {
      const mu = (((parseFloat(val) - fobResult.landed_cost) / fobResult.landed_cost) * 100).toFixed(2);
      setFobWspMarkup(mu);
    } else { setFobWspMarkup(""); }
  };
  const onFobWspMarkupChange = (val) => {
    setFobWspMarkup(val);
    if (val && fobResult) {
      const price = (fobResult.landed_cost * (1 + parseFloat(val) / 100)).toFixed(2);
      setFobWspPrice(price);
    } else { setFobWspPrice(""); }
  };
  const onFobMrpPriceChange = (val) => {
    setFobMrpPrice(val);
    if (val && fobWspPrice) {
      const mu = (((parseFloat(val) - parseFloat(fobWspPrice)) / parseFloat(fobWspPrice)) * 100).toFixed(2);
      setFobMrpMarkup(mu);
    } else { setFobMrpMarkup(""); }
  };
  const onFobMrpMarkupChange = (val) => {
    setFobMrpMarkup(val);
    if (val && fobWspPrice) {
      const price = (parseFloat(fobWspPrice) * (1 + parseFloat(val) / 100)).toFixed(2);
      setFobMrpPrice(price);
    } else { setFobMrpPrice(""); }
  };

  const saveFobDesign = async () => {
    if (!fobDesignName.trim() || !fobResult) return;
    setFobSaving(true);
    try {
      const payload = {
        design_name: fobDesignName.trim(),
        description: fobDesignDesc.trim(),
        form_data: { product_cost: fobProductCost, discount_pct: fobDiscount, trims_cost: fobTrimsCost, border_type: fobBorderType, calc_mode: "fob" },
        result_data: { ...fobResult, border_type: fobBorderType, wsp_price: fobWspPrice ? parseFloat(fobWspPrice) : null, mrp_price: fobMrpPrice ? parseFloat(fobMrpPrice) : null, calc_mode: "fob" },
      };
      if (loadedDesignId) {
        await api.put(`/saved-designs/${loadedDesignId}`, payload);
        toast.success("FOB design updated!");
        setPdfStep(true);
      } else {
        const res = await api.post("/saved-designs", payload);
        toast.success("FOB design saved!");
        setTagStepId(res.data.id);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setFobSaving(false); }
  };

  // Shirt Calculator
  const calculateShirt = () => {
    const fabricRate = parseFloat(shirtFabricRate) || 0;
    const consumption = parseFloat(shirtConsumption) || 0;
    const cmt = parseFloat(shirtCmt) || 0;
    if (!fabricRate || !consumption) { toast.error("Please enter Fabric Rate and Consumption"); return; }
    const costPerShirt = (fabricRate * consumption) + cmt;
    const whPct = fobSettings.warehouse_pct || 5;
    const warehouseCost = costPerShirt * (whPct / 100);
    const landedCost = costPerShirt + warehouseCost;
    setShirtResult({ fabric_rate: fabricRate, consumption, cmt, cost_per_shirt: costPerShirt, warehouse_pct: whPct, warehouse_cost: warehouseCost, landed_cost: landedCost });
    // Preserve WSP/MRP — recompute margin % bands against new landed cost
    if (shirtWspPrice && landedCost > 0) {
      const wsp = parseFloat(shirtWspPrice);
      setShirtWspMarkup((((wsp - landedCost) / landedCost) * 100).toFixed(2));
      setShirtWspMarkdown(wsp > 0 ? (((wsp - landedCost) / wsp) * 100).toFixed(2) : "");
      if (shirtMrpPrice) {
        const mrp = parseFloat(shirtMrpPrice);
        setShirtMrpMarkup(wsp > 0 ? (((mrp - wsp) / wsp) * 100).toFixed(2) : "");
        setShirtMrpMarkdown(mrp > 0 ? (((mrp - wsp) / mrp) * 100).toFixed(2) : "");
      }
    } else if (!shirtWspPrice) {
      setShirtWspMarkup(""); setShirtMrpMarkup("");
      setShirtWspMarkdown(""); setShirtMrpMarkdown("");
    }
    toast.success("Shirt cost calculated!");
  };

  const saveShirtDesign = async () => {
    if (!shirtDesignName.trim() || !shirtResult) return;
    setShirtSaving(true);
    try {
      const payload = {
        design_name: shirtDesignName.trim(),
        description: shirtDesignDesc.trim(),
        form_data: { fabric_rate: shirtFabricRate, consumption: shirtConsumption, cmt: shirtCmt, calc_mode: "shirt" },
        result_data: { ...shirtResult, wsp_price: shirtWspPrice ? parseFloat(shirtWspPrice) : null, mrp_price: shirtMrpPrice ? parseFloat(shirtMrpPrice) : null, calc_mode: "shirt" },
      };
      if (loadedDesignId) {
        await api.put(`/saved-designs/${loadedDesignId}`, payload);
        toast.success("Shirt design updated!");
        setPdfStep(true);
      } else {
        const res = await api.post("/saved-designs", payload);
        toast.success("Shirt design saved!");
        setTagStepId(res.data.id);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setShirtSaving(false); }
  };

  const exportShirtPdf = () => {
    if (!shirtResult) return;
    const r = shirtResult;
    const fabricCost = (r.fabric_rate * r.consumption).toFixed(2);
    const name = shirtDesignName || "Shirt Costing";

    const html = `
    <div style="font-family:'Work Sans','IBM Plex Sans',sans-serif;max-width:700px;padding:0;font-size:11px;color:#1C1917;">
      <div style="text-align:center;margin-bottom:10px;padding:8px 0;">
        <img src="${LOGO_BASE64}" style="height:44px;margin:0 auto;" />
        <div style="font-size:11px;color:#78716c;margin-top:6px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">Shirt Cost Sheet</div>
      </div>
      <div style="background:linear-gradient(135deg,#C2410C,#7C2D12);color:white;border-radius:10px;padding:16px 20px;margin-bottom:12px;">
        <div style="font-size:14px;font-weight:700;text-align:center;margin-bottom:8px;">${name}</div>
        <div style="display:flex;justify-content:space-around;text-align:center;">
          <div><div style="font-size:9px;opacity:0.6;">Landed Cost</div><div style="font-size:20px;font-weight:700;">${r.landed_cost.toFixed(2)}</div></div>
          <div><div style="font-size:9px;opacity:0.6;">WSP</div><div style="font-size:20px;font-weight:700;">${shirtWspPrice || "—"}</div>${shirtWspPrice ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${shirtWspMarkup}% / MD ${shirtWspMarkdown}%</div>` : ""}</div>
          <div><div style="font-size:9px;opacity:0.6;">MRP</div><div style="font-size:20px;font-weight:700;">${shirtMrpPrice || "—"}</div>${shirtMrpPrice ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${shirtMrpMarkup}% / MD ${shirtMrpMarkdown}%</div>` : ""}</div>
        </div>
      </div>
      <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;margin-bottom:12px;">
        <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:6px;">Specifications</div>
        <div style="display:flex;gap:24px;font-size:11px;flex-wrap:wrap;">
          <div><span style="color:#78716c;">Fabric Rate:</span> <b>${r.fabric_rate}/mtr</b></div>
          <div><span style="color:#78716c;">Consumption:</span> <b>${r.consumption}m</b></div>
          <div><span style="color:#78716c;">CMT Charge:</span> <b>${r.cmt}</b></div>
          ${vendorNames.length ? `<div style="width:100%;border-top:1px dashed #E7E5E4;padding-top:6px;margin-top:2px;"><span style="color:#78716c;">Vendors:</span> <b>${vendorNames.join(", ")}</b></div>` : ""}
        </div>
      </div>
      <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;">
        <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:6px;">Cost Breakdown</div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <tr><td style="padding:6px 0;color:#57534E;">Fabric (${r.fabric_rate} × ${r.consumption}m)</td><td style="text-align:right;font-family:monospace;">${fabricCost}</td></tr>
          <tr><td style="padding:6px 0;color:#57534E;">CMT Charge</td><td style="text-align:right;font-family:monospace;">${r.cmt.toFixed(2)}</td></tr>
          <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
          <tr><td style="padding:6px 0;font-weight:600;">Cost / Shirt</td><td style="text-align:right;font-family:monospace;font-weight:600;">${r.cost_per_shirt.toFixed(2)}</td></tr>
          <tr><td style="padding:6px 0;color:#57534E;">Warehouse (${r.warehouse_pct}%)</td><td style="text-align:right;font-family:monospace;">${r.warehouse_cost.toFixed(2)}</td></tr>
          <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
          <tr style="background:#C2410C;color:white;font-weight:700;"><td style="padding:8px;">Landed Cost</td><td style="padding:8px;text-align:right;font-family:monospace;">${r.landed_cost.toFixed(2)}</td></tr>
          ${shirtWspPrice ? `<tr><td style="padding:6px 0;font-weight:600;">WSP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${shirtWspPrice}</td></tr>` : ""}
          ${shirtWspPrice ? `<tr><td style="padding:6px 0;color:#57534E;">WSP Markup / Markdown</td><td style="text-align:right;font-family:monospace;">${shirtWspMarkup}% / ${shirtWspMarkdown}%</td></tr>` : ""}
          ${shirtMrpPrice ? `<tr><td style="padding:6px 0;font-weight:600;">MRP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${shirtMrpPrice}</td></tr>` : ""}
          ${shirtMrpPrice ? `<tr><td style="padding:6px 0;color:#57534E;">MRP Markup / Markdown</td><td style="text-align:right;font-family:monospace;">${shirtMrpMarkup}% / ${shirtMrpMarkdown}%</td></tr>` : ""}
        </table>
      </div>
      ${NEW_WSP_MRP_BLOCK}
      <div style="text-align:center;font-size:9px;color:#a8a29e;margin-top:16px;">Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
    </div>`;

    const container = document.createElement("div");
    container.innerHTML = html;
    document.body.appendChild(container);
    html2pdf().set({
      margin: [8, 8, 8, 8],
      filename: `Shirt_Costing_${name}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.92 },
      html2canvas: { scale: 1.5, useCORS: true, logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    }).from(container).save().then(() => document.body.removeChild(container));
    toast.success("Shirt PDF exported!");
  };






  const exportFobPdf = () => {
    if (!fobResult) return;
    const r = fobResult;
    const name = fobDesignName || "FOB Costing";
    const hasDisc = r.discount_pct > 0;

    const html = `
    <div style="font-family:'Work Sans','IBM Plex Sans',sans-serif;max-width:700px;padding:0;font-size:11px;color:#1C1917;">
      <div style="text-align:center;margin-bottom:10px;padding:8px 0;">
        <img src="${LOGO_BASE64}" style="height:44px;margin:0 auto;" />
        <div style="font-size:11px;color:#78716c;margin-top:6px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">FOB Cost Sheet</div>
      </div>
      <div style="background:linear-gradient(135deg,#C2410C,#7C2D12);color:white;border-radius:10px;padding:16px 20px;margin-bottom:12px;">
        <div style="font-size:14px;font-weight:700;text-align:center;margin-bottom:8px;">${name}</div>
        <div style="display:flex;justify-content:space-around;text-align:center;">
          <div><div style="font-size:9px;opacity:0.6;">Landed Cost</div><div style="font-size:20px;font-weight:700;">${r.landed_cost.toFixed(2)}</div></div>
          <div><div style="font-size:9px;opacity:0.6;">WSP</div><div style="font-size:20px;font-weight:700;">${fobWspPrice || "—"}</div>${fobWspPrice ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${fobWspMarkup}% / MD ${fobWspMarkdown}%</div>` : ""}</div>
          <div><div style="font-size:9px;opacity:0.6;">MRP</div><div style="font-size:20px;font-weight:700;">${fobMrpPrice || "—"}</div>${fobMrpPrice ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${fobMrpMarkup}% / MD ${fobMrpMarkdown}%</div>` : ""}</div>
        </div>
      </div>
      <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;margin-bottom:12px;">
        <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:6px;">FOB Inputs</div>
        <div style="display:flex;gap:24px;flex-wrap:wrap;font-size:11px;">
          <div><span style="color:#78716c;">Product Cost / Dhoti:</span> <b>${r.product_cost.toFixed(2)}</b></div>
          ${hasDisc ? `<div><span style="color:#78716c;">Discount:</span> <b>${r.discount_pct}%</b></div>` : ""}
          <div><span style="color:#78716c;">Trims Cost / Dhoti:</span> <b>${r.trims_cost.toFixed(2)}</b></div>
          <div><span style="color:#78716c;">Border:</span> <b>${fobBorderType || "—"}</b></div>
          ${vendorNames.length ? `<div style="width:100%;border-top:1px dashed #E7E5E4;padding-top:6px;margin-top:2px;"><span style="color:#78716c;">Vendors:</span> <b>${vendorNames.join(", ")}</b></div>` : ""}
        </div>
      </div>
      <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;">
        <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:6px;">Cost Breakdown</div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <tr><td style="padding:6px 0;color:#57534E;">Product Cost</td><td style="text-align:right;font-family:monospace;">${r.product_cost.toFixed(2)}</td></tr>
          ${hasDisc ? `<tr><td style="padding:6px 0;color:#57534E;">Discount (${r.discount_pct}%)</td><td style="text-align:right;font-family:monospace;color:#B91C1C;">-${r.discount_amount.toFixed(2)}</td></tr>` : ""}
          ${hasDisc ? `<tr><td style="padding:6px 0;color:#57534E;">After Discount</td><td style="text-align:right;font-family:monospace;">${r.discounted_product_cost.toFixed(2)}</td></tr>` : ""}
          <tr><td style="padding:6px 0;color:#57534E;">Trims Cost</td><td style="text-align:right;font-family:monospace;">${r.trims_cost.toFixed(2)}</td></tr>
          <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
          <tr><td style="padding:6px 0;font-weight:600;">Cost / Dhoti</td><td style="text-align:right;font-family:monospace;font-weight:600;">${r.cost_per_dhoti.toFixed(2)}</td></tr>
          <tr><td style="padding:6px 0;color:#57534E;">Warehouse (${r.warehouse_pct}%)</td><td style="text-align:right;font-family:monospace;">${r.warehouse_cost.toFixed(2)}</td></tr>
          <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
          <tr style="background:#C2410C;color:white;font-weight:700;"><td style="padding:8px;">Landed Cost</td><td style="padding:8px;text-align:right;font-family:monospace;">${r.landed_cost.toFixed(2)}</td></tr>
          ${fobWspPrice ? `<tr><td style="padding:6px 0;font-weight:600;">WSP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${fobWspPrice}</td></tr>` : ""}
          ${fobWspPrice ? `<tr><td style="padding:6px 0;color:#57534E;">WSP Markup / Markdown</td><td style="text-align:right;font-family:monospace;">${fobWspMarkup}% / ${fobWspMarkdown}%</td></tr>` : ""}
          ${fobMrpPrice ? `<tr><td style="padding:6px 0;font-weight:600;">MRP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${fobMrpPrice}</td></tr>` : ""}
          ${fobMrpPrice ? `<tr><td style="padding:6px 0;color:#57534E;">MRP Markup / Markdown</td><td style="text-align:right;font-family:monospace;">${fobMrpMarkup}% / ${fobMrpMarkdown}%</td></tr>` : ""}
        </table>
      </div>
      ${NEW_WSP_MRP_BLOCK}
      <div style="text-align:center;font-size:9px;color:#a8a29e;margin-top:12px;">Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
    </div>`;

    const container = document.createElement("div");
    container.innerHTML = html;
    document.body.appendChild(container);
    html2pdf().set({
      margin: [8, 8, 8, 8],
      filename: `FOB_Costing_${name}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.92 },
      html2canvas: { scale: 1.5, useCORS: true, logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    }).from(container).save().then(() => document.body.removeChild(container));
    toast.success("FOB PDF exported!");
  };




  const handleCalculate = async () => {
    const required = ["fabric_width", "border_width", "reed", "pick", "pick_rate", "size", "border_type", "processing_type", "warp_count", "warp_quality", "weft_count", "weft_quality", "kuri_count", "kuri_quality"];
    const missing = required.filter((k) => !form[k]);
    if (missing.length > 0) {
      toast.error(`Please fill all required fields: ${missing.join(", ")}`);
      return;
    }
    if (calcMode === "tissue" && (!tissueWeft2Count || !tissueWeft2Quality)) {
      toast.error("Please select Weft 2 Count and Quality for Tissue mode");
      return;
    }

    setCalculating(true);
    try {
      const payload = {
        fabric_width: parseFloat(form.fabric_width),
        border_width: parseFloat(form.border_width),
        reed: parseFloat(form.reed),
        pick: parseFloat(form.pick),
        pick_rate: parseFloat(form.pick_rate),
        size: parseFloat(form.size),
        border_type: form.border_type,
        bobbin_type: form.bobbin_type,
        processing_type: form.processing_type,
        warp_count: form.warp_count,
        warp_quality: form.warp_quality,
        weft_count: form.weft_count,
        weft_quality: form.weft_quality,
        bobbin1_count: form.bobbin1_count || null,
        bobbin1_quality: form.bobbin1_quality || null,
        bobbin2_count: form.bobbin2_count || null,
        bobbin2_quality: form.bobbin2_quality || null,
        kuri_count: form.kuri_count,
        kuri_quality: form.kuri_quality,
        trims_cost: parseFloat(form.trims_cost) || 0,
        calc_mode: calcMode,
        weft2_count: calcMode === "tissue" ? tissueWeft2Count : null,
        weft2_quality: calcMode === "tissue" ? tissueWeft2Quality : null,
      };
      const res = await api.post("/calculate", payload);
      setResult(res.data);
      setShowDetails(true);
      // Preserve WSP/MRP from item master / prior tagging — just recompute the margin % bands
      // against the new landed cost. Prices stay editable.
      const newLanded = Number(res.data?.final_cost_per_dhoti || res.data?.landed_cost || 0);
      if (wspPrice && newLanded > 0) {
        const wsp = parseFloat(wspPrice);
        setWspMarkup((((wsp - newLanded) / newLanded) * 100).toFixed(2));
        setWspMarkdown(wsp > 0 ? (((wsp - newLanded) / wsp) * 100).toFixed(2) : "");
        if (mrpPrice) {
          const mrp = parseFloat(mrpPrice);
          setMrpMarkup(wsp > 0 ? (((mrp - wsp) / wsp) * 100).toFixed(2) : "");
          setMrpMarkdown(mrp > 0 ? (((mrp - wsp) / mrp) * 100).toFixed(2) : "");
        }
      } else if (!wspPrice) {
        // No prior price — clear bands only
        setWspMarkup(""); setWspMarkdown("");
        setMrpMarkup(""); setMrpMarkdown("");
      }
      toast.success("Cost calculated successfully!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCalculating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const noMasterData = yarnData.counts.length === 0 || borderTypes.length === 0;

  return (
    <>
    <div className="space-y-6 pb-24 md:pb-8" data-testid="costing-calculator-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
            Dhoti Costing Calculator
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {calcMode === "regular" ? "Calculate production cost per dhoti based on yarn specifications." : calcMode === "fob" ? "Calculate finished goods purchase cost (FOB)." : calcMode === "shirt" ? "Calculate shirt cost from fabric rate and CMT." : "Calculate tissue cost with dual weft yarns."}
          </p>
        </div>
        <div className="flex bg-stone-100 rounded-lg p-0.5 shrink-0" data-testid="calc-mode-toggle">
          <button
            data-testid="mode-regular"
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${calcMode === "regular" ? "bg-white text-[#C2410C] shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
            onClick={() => setCalcMode("regular")}
          >
            Regular
          </button>
          <button
            data-testid="mode-fob"
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${calcMode === "fob" ? "bg-white text-[#C2410C] shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
            onClick={() => setCalcMode("fob")}
          >
            FOB
          </button>
          <button
            data-testid="mode-shirt"
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${calcMode === "shirt" ? "bg-white text-[#C2410C] shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
            onClick={() => setCalcMode("shirt")}
          >
            Shirts
          </button>
          <button
            data-testid="mode-tissue"
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${calcMode === "tissue" ? "bg-white text-[#C2410C] shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
            onClick={() => setCalcMode("tissue")}
          >
            Tissue
          </button>
        </div>
      </div>

      {calcMode === "shirt" ? (
        /* Shirt Calculator UI */
        <div className="max-w-xl space-y-6">
          <div className="bg-white border border-stone-200 rounded-lg p-5 sm:p-6 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-2">Shirt Cost Inputs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Fabric Rate / mtr</Label>
                <Input data-testid="shirt-fabric-rate" type="number" step="0.01" placeholder="e.g. 250" value={shirtFabricRate} onChange={(e) => setShirtFabricRate(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Consumption (mtr)</Label>
                <Input data-testid="shirt-consumption" type="number" step="0.01" placeholder="e.g. 1.5" value={shirtConsumption} onChange={(e) => setShirtConsumption(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">CMT Charge</Label>
                <Input data-testid="shirt-cmt" type="number" step="0.01" placeholder="e.g. 80" value={shirtCmt} onChange={(e) => setShirtCmt(e.target.value)} />
              </div>
            </div>
            <Button data-testid="shirt-calculate-button" className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white h-11" onClick={calculateShirt}>
              <Calculator className="h-4 w-4 mr-2" /> Calculate Shirt Cost
            </Button>
          </div>

          {shirtResult && (
            <>
              <div className="bg-white border border-stone-200 rounded-lg p-5 sm:p-6">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">Cost Breakdown</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-stone-500">Fabric ({shirtResult.fabric_rate} × {shirtResult.consumption}m)</span><span className="font-mono">{(shirtResult.fabric_rate * shirtResult.consumption).toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-500">CMT Charge</span><span className="font-mono">{shirtResult.cmt.toFixed(2)}</span></div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-semibold"><span>Cost / Shirt</span><span className="font-mono">{shirtResult.cost_per_shirt.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-500">Warehouse ({shirtResult.warehouse_pct}%)</span><span className="font-mono">{shirtResult.warehouse_cost.toFixed(2)}</span></div>
                  <Separator className="my-2" />
                  <div className="flex justify-between text-base font-bold text-[#C2410C]"><span>Landed Cost</span><span className="font-mono">{shirtResult.landed_cost.toFixed(2)}</span></div>
                </div>
              </div>

              {/* WSP/MRP - Full pricing */}
              <div className="bg-white border border-stone-200 rounded-lg p-5 sm:p-6">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">WSP & MRP Pricing</h3>
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-2 block">WSP (from Landed Cost: {shirtResult.landed_cost.toFixed(2)})</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div><span className="text-xs text-stone-400">Markup %</span>
                        <Input data-testid="shirt-wsp-markup" type="number" step="0.01" value={shirtWspMarkup} onChange={(e) => { const v=e.target.value; setShirtWspMarkup(v); setShirtWspMarkdown(""); if(v&&shirtResult){const p=Math.round(shirtResult.landed_cost*(1+parseFloat(v)/100));setShirtWspPrice(p);setShirtWspMarkdown((((p-shirtResult.landed_cost)/p)*100).toFixed(2));setShirtMrpMarkup("");setShirtMrpMarkdown("");setShirtMrpPrice("");}else{setShirtWspPrice("");}}} className="h-9 mt-1" />
                      </div>
                      <div><span className="text-xs text-stone-400">Markdown %</span>
                        <Input type="number" step="0.01" value={shirtWspMarkdown} onChange={(e) => { const v=e.target.value; setShirtWspMarkdown(v); setShirtWspMarkup(""); if(v&&shirtResult){const md=parseFloat(v);if(md<100){const p=Math.round(shirtResult.landed_cost/(1-md/100));setShirtWspPrice(p);setShirtWspMarkup((((p-shirtResult.landed_cost)/shirtResult.landed_cost)*100).toFixed(2));setShirtMrpMarkup("");setShirtMrpMarkdown("");setShirtMrpPrice("");}}else{setShirtWspPrice("");}}} className="h-9 mt-1" />
                      </div>
                      <div><span className="text-xs text-stone-400">WSP Price</span>
                        <Input data-testid="shirt-wsp-price" type="number" step="1" value={shirtWspPrice} onChange={(e) => { const p=e.target.value; setShirtWspPrice(p); if(p&&shirtResult){const pr=parseFloat(p);if(pr>0){setShirtWspMarkup((((pr-shirtResult.landed_cost)/shirtResult.landed_cost)*100).toFixed(2));setShirtWspMarkdown((((pr-shirtResult.landed_cost)/pr)*100).toFixed(2));setShirtMrpMarkup("");setShirtMrpMarkdown("");setShirtMrpPrice("");}}else{setShirtWspMarkup("");setShirtWspMarkdown("");}}} className="h-9 mt-1 font-semibold" />
                      </div>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-2 block">MRP (from WSP: {shirtWspPrice || "—"})</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div><span className="text-xs text-stone-400">Markup %</span>
                        <Input data-testid="shirt-mrp-markup" type="number" step="0.01" value={shirtMrpMarkup} onChange={(e) => { const v=e.target.value; setShirtMrpMarkup(v); setShirtMrpMarkdown(""); const wsp=parseFloat(shirtWspPrice); if(v&&wsp>0){const p=Math.round(wsp*(1+parseFloat(v)/100)/5)*5;setShirtMrpPrice(p);setShirtMrpMarkdown((((p-wsp)/p)*100).toFixed(2));}else{setShirtMrpPrice("");}}} className="h-9 mt-1" disabled={!shirtWspPrice} />
                      </div>
                      <div><span className="text-xs text-stone-400">Markdown %</span>
                        <Input type="number" step="0.01" value={shirtMrpMarkdown} onChange={(e) => { const v=e.target.value; setShirtMrpMarkdown(v); setShirtMrpMarkup(""); const wsp=parseFloat(shirtWspPrice); if(v&&wsp>0){const md=parseFloat(v);if(md<100){const p=Math.round(wsp/(1-md/100)/5)*5;setShirtMrpPrice(p);setShirtMrpMarkup((((p-wsp)/wsp)*100).toFixed(2));}}else{setShirtMrpPrice("");}}} className="h-9 mt-1" disabled={!shirtWspPrice} />
                      </div>
                      <div><span className="text-xs text-stone-400">MRP Price</span>
                        <Input data-testid="shirt-mrp-price" type="number" step="5" value={shirtMrpPrice} onChange={(e) => { const p=e.target.value; setShirtMrpPrice(p); const wsp=parseFloat(shirtWspPrice); if(p&&wsp>0){const pr=parseFloat(p);if(pr>0){setShirtMrpMarkup((((pr-wsp)/wsp)*100).toFixed(2));setShirtMrpMarkdown((((pr-wsp)/pr)*100).toFixed(2));}}else{setShirtMrpMarkup("");setShirtMrpMarkdown("");}}} className="h-9 mt-1 font-semibold" disabled={!shirtWspPrice} />
                      </div>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-2 block">Calculate WSP from MRP</Label>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div><span className="text-xs text-stone-400">MRP</span><Input type="number" step="1" value={shirtRevMrp} onChange={(e) => {setShirtRevMrp(e.target.value);setShirtRevWspResult("");setShirtRevMu("");setShirtRevMd("");}} className="h-9 mt-1" /></div>
                      <div><span className="text-xs text-stone-400">Markup %</span><Input type="number" step="0.01" value={shirtRevMu} onChange={(e) => {const v=e.target.value;setShirtRevMu(v);setShirtRevMd("");const mrp=parseFloat(shirtRevMrp);if(v&&mrp>0){const w=Math.round(mrp/(1+parseFloat(v)/100));setShirtRevWspResult(w);setShirtRevMd((((mrp-w)/mrp)*100).toFixed(2));}else{setShirtRevWspResult("");}}} className="h-9 mt-1" disabled={!shirtRevMrp} /></div>
                      <div><span className="text-xs text-stone-400">Markdown %</span><Input type="number" step="0.01" value={shirtRevMd} onChange={(e) => {const v=e.target.value;setShirtRevMd(v);setShirtRevMu("");const mrp=parseFloat(shirtRevMrp);if(v&&mrp>0){const w=Math.round(mrp*(1-parseFloat(v)/100));setShirtRevWspResult(w);setShirtRevMu((((mrp-w)/w)*100).toFixed(2));}else{setShirtRevWspResult("");}}} className="h-9 mt-1" disabled={!shirtRevMrp} /></div>
                    </div>
                    {shirtRevWspResult && (<div className="bg-stone-50 rounded-md p-3 text-center"><span className="text-xs text-stone-500">Calculated WSP: </span><span className="font-heading font-bold text-lg text-[#C2410C]">{shirtRevWspResult}</span></div>)}
                  </div>
                </div>
              </div>

              {/* Save + Export */}
              <div className="flex gap-3">
                <Button data-testid="shirt-save-button" title="Ctrl/Cmd+S" className="flex-1 bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={() => setShirtSaveOpen(true)}>
                  <Save className="h-4 w-4 mr-2" /> {loadedDesignId ? "Update Design" : "Save Design"} <span className="ml-2 text-[10px] opacity-70">(⌘S)</span>
                </Button>
                <Button data-testid="shirt-export-pdf" variant="outline" className="border-[#C2410C] text-[#C2410C] hover:bg-orange-50" onClick={exportShirtPdf}>
                  <FileDown className="h-4 w-4 mr-2" /> Export PDF
                </Button>
              </div>
            </>
          )}

          {/* Shirt Save Dialog */}
          <Dialog open={shirtSaveOpen} onOpenChange={(o) => { if (!o) { setTagStepId(null); setPdfStep(false); } setShirtSaveOpen(o); }}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>{pdfStep ? "Export PDF?" : tagStepId ? "Tag Barcode (Optional)" : loadedDesignId ? "Update Shirt Design" : "Save Shirt Design"}</DialogTitle></DialogHeader>
              {pdfStep ? (
                <PdfPromptStep
                  onExport={() => {
                    setPdfStep(false); setShirtSaveOpen(false);
                    // Defer to next paint so the dialog fully unmounts before html2pdf renders
                    setTimeout(() => {
                      document.querySelector('[data-testid="shirt-export-pdf"]')?.click();
                      setTimeout(() => resetShirt(), 300);
                    }, 60);
                  }}
                  onSkip={() => { setPdfStep(false); setShirtSaveOpen(false); resetShirt(); }}
                />
              ) : tagStepId ? (
                <TagBarcodePicker
                  designId={tagStepId}
                  onTagged={async () => { const id = tagStepId; setTagStepId(null); await refreshAfterTag(id, "shirt"); setPdfStep(true); }}
                  onSkip={() => { setTagStepId(null); setPdfStep(true); }}
                />
              ) : (
                <>
                  <div className="space-y-3">
                    <div><Label className="text-xs mb-1 block">Design Name</Label><Input data-testid="shirt-design-name" value={shirtDesignName} onChange={(e) => setShirtDesignName(e.target.value)} placeholder="Enter design name" /></div>
                    <div><Label className="text-xs mb-1 block">Description</Label><Input value={shirtDesignDesc} onChange={(e) => setShirtDesignDesc(e.target.value)} placeholder="Optional description" /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShirtSaveOpen(false)}>Cancel</Button>
                    <Button data-testid="shirt-save-confirm" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" disabled={!shirtDesignName.trim() || shirtSaving} onClick={saveShirtDesign}>
                      {shirtSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} {loadedDesignId ? "Update" : "Save"}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      ) : calcMode === "fob" ? (
        <div className="max-w-xl space-y-6">
          <div className="bg-white border border-stone-200 rounded-lg p-5 sm:p-6 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-2">FOB Cost Inputs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Product Cost / Dhoti</Label>
                <Input data-testid="fob-product-cost" type="number" step="0.01" placeholder="e.g. 120" value={fobProductCost} onChange={(e) => setFobProductCost(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Discount %</Label>
                <Input data-testid="fob-discount" type="number" step="0.01" placeholder="e.g. 5" value={fobDiscount} onChange={(e) => setFobDiscount(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Trims Cost / Dhoti</Label>
                <Input data-testid="fob-trims-cost" type="number" step="0.01" placeholder="e.g. 8" value={fobTrimsCost} onChange={(e) => setFobTrimsCost(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Border Type</Label>
                <Select value={fobBorderType} onValueChange={setFobBorderType}>
                  <SelectTrigger data-testid="fob-border-type"><SelectValue placeholder="Select border" /></SelectTrigger>
                  <SelectContent>{borderTypes.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="fob-calculate-button" className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white h-11" onClick={calculateFob}>
              <Calculator className="h-4 w-4 mr-2" /> Calculate FOB Cost
            </Button>
          </div>

          {fobResult && (
            <>
              <div className="bg-white border border-stone-200 rounded-lg p-5 sm:p-6">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">Cost Breakdown</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-stone-500">Product Cost</span><span className="font-mono">{fobResult.product_cost.toFixed(2)}</span></div>
                  {fobResult.discount_pct > 0 && (
                    <div className="flex justify-between"><span className="text-stone-500">Discount ({fobResult.discount_pct}%)</span><span className="font-mono text-red-600">-{fobResult.discount_amount.toFixed(2)}</span></div>
                  )}
                  {fobResult.discount_pct > 0 && (
                    <div className="flex justify-between"><span className="text-stone-500">After Discount</span><span className="font-mono">{fobResult.discounted_product_cost.toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-stone-500">Trims Cost</span><span className="font-mono">{fobResult.trims_cost.toFixed(2)}</span></div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-semibold"><span>Cost / Dhoti</span><span className="font-mono">{fobResult.cost_per_dhoti.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-500">Warehouse ({fobResult.warehouse_pct}%)</span><span className="font-mono">{fobResult.warehouse_cost.toFixed(2)}</span></div>
                  <Separator className="my-2" />
                  <div className="flex justify-between text-base font-bold text-[#C2410C]"><span>Landed Cost</span><span className="font-mono">{fobResult.landed_cost.toFixed(2)}</span></div>
                </div>
              </div>

              {/* WSP/MRP - Full pricing matching regular calculator */}
              <div className="bg-white border border-stone-200 rounded-lg p-5 sm:p-6">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">WSP & MRP Pricing</h3>
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-2 block">WSP (from Landed Cost: {fobResult.landed_cost.toFixed(2)})</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <span className="text-xs text-stone-400">Markup %</span>
                        <Input data-testid="fob-wsp-markup" type="number" step="0.01" placeholder="e.g. 50"
                          value={fobWspMarkup}
                          onChange={(e) => {
                            const v = e.target.value; setFobWspMarkup(v); setFobWspMarkdown("");
                            if (v && fobResult) {
                              const p = Math.round(fobResult.landed_cost * (1 + parseFloat(v) / 100));
                              setFobWspPrice(p); setFobWspMarkdown((((p - fobResult.landed_cost) / p) * 100).toFixed(2));
                              setFobMrpMarkup(""); setFobMrpMarkdown(""); setFobMrpPrice("");
                            } else { setFobWspPrice(""); }
                          }}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">Markdown %</span>
                        <Input data-testid="fob-wsp-markdown" type="number" step="0.01" placeholder="e.g. 33"
                          value={fobWspMarkdown}
                          onChange={(e) => {
                            const v = e.target.value; setFobWspMarkdown(v); setFobWspMarkup("");
                            if (v && fobResult) {
                              const md = parseFloat(v);
                              if (md < 100) {
                                const p = Math.round(fobResult.landed_cost / (1 - md / 100));
                                setFobWspPrice(p); setFobWspMarkup((((p - fobResult.landed_cost) / fobResult.landed_cost) * 100).toFixed(2));
                                setFobMrpMarkup(""); setFobMrpMarkdown(""); setFobMrpPrice("");
                              }
                            } else { setFobWspPrice(""); }
                          }}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">WSP Price</span>
                        <Input data-testid="fob-wsp-price" type="number" step="1" placeholder="Price"
                          value={fobWspPrice}
                          onChange={(e) => {
                            const p = e.target.value; setFobWspPrice(p);
                            if (p && fobResult) {
                              const price = parseFloat(p);
                              if (price > 0 && fobResult.landed_cost > 0) {
                                setFobWspMarkup((((price - fobResult.landed_cost) / fobResult.landed_cost) * 100).toFixed(2));
                                setFobWspMarkdown((((price - fobResult.landed_cost) / price) * 100).toFixed(2));
                                setFobMrpMarkup(""); setFobMrpMarkdown(""); setFobMrpPrice("");
                              }
                            } else { setFobWspMarkup(""); setFobWspMarkdown(""); }
                          }}
                          className="h-9 mt-1 font-semibold"
                        />
                      </div>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-2 block">MRP (from WSP: {fobWspPrice || "—"})</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <span className="text-xs text-stone-400">Markup %</span>
                        <Input data-testid="fob-mrp-markup" type="number" step="0.01" placeholder="e.g. 40"
                          value={fobMrpMarkup}
                          onChange={(e) => {
                            const v = e.target.value; setFobMrpMarkup(v); setFobMrpMarkdown("");
                            const wsp = parseFloat(fobWspPrice);
                            if (v && wsp > 0) {
                              const p = Math.round(wsp * (1 + parseFloat(v) / 100) / 5) * 5;
                              setFobMrpPrice(p); setFobMrpMarkdown((((p - wsp) / p) * 100).toFixed(2));
                            } else { setFobMrpPrice(""); }
                          }}
                          className="h-9 mt-1" disabled={!fobWspPrice}
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">Markdown %</span>
                        <Input type="number" step="0.01" placeholder="e.g. 28"
                          value={fobMrpMarkdown}
                          onChange={(e) => {
                            const v = e.target.value; setFobMrpMarkdown(v); setFobMrpMarkup("");
                            const wsp = parseFloat(fobWspPrice);
                            if (v && wsp > 0) {
                              const md = parseFloat(v);
                              if (md < 100) {
                                const p = Math.round(wsp / (1 - md / 100) / 5) * 5;
                                setFobMrpPrice(p); setFobMrpMarkup((((p - wsp) / wsp) * 100).toFixed(2));
                              }
                            } else { setFobMrpPrice(""); }
                          }}
                          className="h-9 mt-1" disabled={!fobWspPrice}
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">MRP Price</span>
                        <Input data-testid="fob-mrp-price" type="number" step="5" placeholder="Price"
                          value={fobMrpPrice}
                          onChange={(e) => {
                            const p = e.target.value; setFobMrpPrice(p);
                            const wsp = parseFloat(fobWspPrice);
                            if (p && wsp > 0) {
                              const price = parseFloat(p);
                              if (price > 0) {
                                setFobMrpMarkup((((price - wsp) / wsp) * 100).toFixed(2));
                                setFobMrpMarkdown((((price - wsp) / price) * 100).toFixed(2));
                              }
                            } else { setFobMrpMarkup(""); setFobMrpMarkdown(""); }
                          }}
                          className="h-9 mt-1 font-semibold" disabled={!fobWspPrice}
                        />
                      </div>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-2 block">Calculate WSP from MRP</Label>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div>
                        <span className="text-xs text-stone-400">MRP</span>
                        <Input data-testid="fob-rev-mrp" type="number" step="1" placeholder="Enter MRP"
                          value={fobRevMrp}
                          onChange={(e) => { setFobRevMrp(e.target.value); setFobRevWspResult(""); setFobRevMu(""); setFobRevMd(""); }}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">Markup %</span>
                        <Input type="number" step="0.01" placeholder="e.g. 40"
                          value={fobRevMu}
                          onChange={(e) => {
                            const v = e.target.value; setFobRevMu(v); setFobRevMd("");
                            const mrp = parseFloat(fobRevMrp);
                            if (v && mrp > 0) {
                              const w = Math.round(mrp / (1 + parseFloat(v) / 100));
                              setFobRevWspResult(w); setFobRevMd((((mrp - w) / mrp) * 100).toFixed(2));
                            } else { setFobRevWspResult(""); }
                          }}
                          className="h-9 mt-1" disabled={!fobRevMrp}
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">Markdown %</span>
                        <Input type="number" step="0.01" placeholder="e.g. 28"
                          value={fobRevMd}
                          onChange={(e) => {
                            const v = e.target.value; setFobRevMd(v); setFobRevMu("");
                            const mrp = parseFloat(fobRevMrp);
                            if (v && mrp > 0) {
                              const w = Math.round(mrp * (1 - parseFloat(v) / 100));
                              setFobRevWspResult(w); setFobRevMu((((mrp - w) / w) * 100).toFixed(2));
                            } else { setFobRevWspResult(""); }
                          }}
                          className="h-9 mt-1" disabled={!fobRevMrp}
                        />
                      </div>
                    </div>
                    {fobRevWspResult && (
                      <div className="bg-stone-50 rounded-md p-3 text-center">
                        <span className="text-xs text-stone-500">Calculated WSP: </span>
                        <span className="font-heading font-bold text-lg text-[#C2410C]">{fobRevWspResult}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Save + Export */}
              <div className="flex gap-3">
                <Button data-testid="fob-save-button" title="Ctrl/Cmd+S" className="flex-1 bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={() => setFobSaveOpen(true)}>
                  <Save className="h-4 w-4 mr-2" /> {loadedDesignId ? "Update Design" : "Save Design"} <span className="ml-2 text-[10px] opacity-70">(⌘S)</span>
                </Button>
                <Button data-testid="fob-export-pdf" variant="outline" className="border-[#C2410C] text-[#C2410C] hover:bg-orange-50" onClick={exportFobPdf}>
                  <FileDown className="h-4 w-4 mr-2" /> Export PDF
                </Button>
              </div>
            </>
          )}

          {/* FOB Save Dialog */}
          <Dialog open={fobSaveOpen} onOpenChange={(o) => { if (!o) { setTagStepId(null); setPdfStep(false); } setFobSaveOpen(o); }}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>{pdfStep ? "Export PDF?" : tagStepId ? "Tag Barcode (Optional)" : loadedDesignId ? "Update FOB Design" : "Save FOB Design"}</DialogTitle></DialogHeader>
              {pdfStep ? (
                <PdfPromptStep
                  onExport={() => {
                    setPdfStep(false); setFobSaveOpen(false);
                    setTimeout(() => {
                      document.querySelector('[data-testid="fob-export-pdf"]')?.click();
                      setTimeout(() => resetFob(), 300);
                    }, 60);
                  }}
                  onSkip={() => { setPdfStep(false); setFobSaveOpen(false); resetFob(); }}
                />
              ) : tagStepId ? (
                <TagBarcodePicker
                  designId={tagStepId}
                  onTagged={async () => { const id = tagStepId; setTagStepId(null); await refreshAfterTag(id, "fob"); setPdfStep(true); }}
                  onSkip={() => { setTagStepId(null); setPdfStep(true); }}
                />
              ) : (
                <>
                  <div className="space-y-3">
                    <div><Label className="text-xs mb-1 block">Design Name</Label><Input data-testid="fob-design-name" value={fobDesignName} onChange={(e) => setFobDesignName(e.target.value)} placeholder="Enter design name" /></div>
                    <div><Label className="text-xs mb-1 block">Description</Label><Input value={fobDesignDesc} onChange={(e) => setFobDesignDesc(e.target.value)} placeholder="Optional description" /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setFobSaveOpen(false)}>Cancel</Button>
                    <Button data-testid="fob-save-confirm" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" disabled={!fobDesignName.trim() || fobSaving} onClick={saveFobDesign}>
                      {fobSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} {loadedDesignId ? "Update" : "Save"}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      ) : (
      <>
      {/* Regular Calculator - existing content below */}

      {noMasterData && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          Please upload Yarn Master and Border Master data first before using the calculator.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Primary Specs */}
          <div className="bg-white border border-stone-200 rounded-lg p-5 sm:p-6">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">
              Primary Specifications
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Fabric Width</Label>
                <Input data-testid="fabric-width-input" type="number" step="0.01" placeholder="e.g. 52" value={form.fabric_width} onChange={(e) => updateForm("fabric_width", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Border Width</Label>
                <Input data-testid="border-width-input" type="number" step="0.01" placeholder="e.g. 1" value={form.border_width} onChange={(e) => updateForm("border_width", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Loom Reed</Label>
                <Input data-testid="reed-input" type="number" step="0.01" placeholder="e.g. 80" value={form.reed} onChange={(e) => updateForm("reed", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Loom Pick</Label>
                <Input data-testid="pick-input" type="number" step="0.01" placeholder="e.g. 76" value={form.pick} onChange={(e) => updateForm("pick", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Pick Rate</Label>
                <Input data-testid="pick-rate-input" type="number" step="0.001" placeholder="e.g. 0.18" value={form.pick_rate} onChange={(e) => updateForm("pick_rate", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Size (mtrs)</Label>
                <Input data-testid="size-input" type="number" step="0.01" placeholder="e.g. 2" value={form.size} onChange={(e) => updateForm("size", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Border Type</Label>
                <Select value={form.border_type} onValueChange={(v) => updateForm("border_type", v)}>
                  <SelectTrigger data-testid="border-type-select">
                    <SelectValue placeholder="Select border" />
                  </SelectTrigger>
                  <SelectContent>
                    {borderTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Processing / Folding</Label>
                <Select value={form.processing_type} onValueChange={(v) => updateForm("processing_type", v)}>
                  <SelectTrigger data-testid="processing-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {processingTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Trims Cost / Dhoti</Label>
                <Input data-testid="trims-cost-input" type="number" step="0.01" placeholder="e.g. 5.00" value={form.trims_cost} onChange={(e) => updateForm("trims_cost", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Bobbin Type Toggle */}
          <div className="bg-white border border-stone-200 rounded-lg p-5 sm:p-6">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">
              Bobbin Yarn Type
            </h3>
            <div className="flex gap-2" data-testid="bobbin-type-toggle">
              {[
                { value: "polyester", label: "Polyester" },
                { value: "jari", label: "Jari" },
                { value: "both", label: "Both" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  data-testid={`bobbin-type-${opt.value}`}
                  onClick={() => updateForm("bobbin_type", opt.value)}
                  className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                    form.bobbin_type === opt.value
                      ? "bg-[#C2410C] text-white shadow-sm"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Yarn Selection */}
          <div className="bg-white border border-stone-200 rounded-lg p-5 sm:p-6 space-y-5">
            {/* Warp */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">Warp Yarn</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Count</Label>
                  <Select value={form.warp_count} onValueChange={(v) => selectCountAndAutoFill("warp_count", "warp_quality", v)}>
                    <SelectTrigger data-testid="warp-count-select"><SelectValue placeholder="Count" /></SelectTrigger>
                    <SelectContent>{yarnData.counts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Quality</Label>
                  <Select value={form.warp_quality} onValueChange={(v) => updateForm("warp_quality", v)}>
                    <SelectTrigger data-testid="warp-quality-select"><SelectValue placeholder="Quality" /></SelectTrigger>
                    <SelectContent>{getQualitiesForCount(form.warp_count).map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Weft */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">{calcMode === "tissue" ? "Weft 1 Yarn" : "Weft Yarn"}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Count</Label>
                  <Select value={form.weft_count} onValueChange={(v) => selectCountAndAutoFill("weft_count", "weft_quality", v)}>
                    <SelectTrigger data-testid="weft-count-select"><SelectValue placeholder="Count" /></SelectTrigger>
                    <SelectContent>{yarnData.counts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Quality</Label>
                  <Select value={form.weft_quality} onValueChange={(v) => updateForm("weft_quality", v)}>
                    <SelectTrigger data-testid="weft-quality-select"><SelectValue placeholder="Quality" /></SelectTrigger>
                    <SelectContent>{getQualitiesForCount(form.weft_count).map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {calcMode === "tissue" && (
              <>
              <Separator />
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">Weft 2 Yarn</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Count</Label>
                    <Select value={tissueWeft2Count} onValueChange={(v) => { setTissueWeft2Count(v); const qs = yarnData.items.filter((i) => i.count === v); if (qs.length === 1) setTissueWeft2Quality(qs[0].quality); else setTissueWeft2Quality(""); }}>
                      <SelectTrigger data-testid="weft2-count-select"><SelectValue placeholder="Count" /></SelectTrigger>
                      <SelectContent>{yarnData.counts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Quality</Label>
                    <Select value={tissueWeft2Quality} onValueChange={setTissueWeft2Quality}>
                      <SelectTrigger data-testid="weft2-quality-select"><SelectValue placeholder="Quality" /></SelectTrigger>
                      <SelectContent>{getQualitiesForCount(tissueWeft2Count).map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              </>
            )}

            <Separator />

            {/* Kuri */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">Kuri Yarn</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Count</Label>
                  <Select value={form.kuri_count} onValueChange={(v) => selectCountAndAutoFill("kuri_count", "kuri_quality", v)}>
                    <SelectTrigger data-testid="kuri-count-select"><SelectValue placeholder="Count" /></SelectTrigger>
                    <SelectContent>{yarnData.counts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Quality</Label>
                  <Select value={form.kuri_quality} onValueChange={(v) => updateForm("kuri_quality", v)}>
                    <SelectTrigger data-testid="kuri-quality-select"><SelectValue placeholder="Quality" /></SelectTrigger>
                    <SelectContent>{getQualitiesForCount(form.kuri_count).map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              data-testid="calculate-button"
              className="flex-1 h-12 bg-[#C2410C] hover:bg-[#9A3412] text-white font-medium text-base"
              onClick={handleCalculate}
              disabled={calculating || noMasterData}
            >
              {calculating ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Calculator className="h-5 w-5 mr-2" />}
              {calculating ? "Calculating..." : "Calculate Dhoti Cost"}
            </Button>
            <Button
              data-testid="save-design-button"
              variant="outline"
              title="Ctrl/Cmd+S"
              className="h-12 px-5 border-stone-300 text-stone-700 hover:bg-stone-100"
              onClick={() => setSaveOpen(true)}
            >
              <Save className="h-5 w-5 mr-2" />
              {loadedDesignId ? "Update" : "Save"} <span className="ml-2 text-[10px] opacity-70">(⌘S)</span>
            </Button>
          </div>
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-1">
          {result ? (
            <div className="sticky top-4 space-y-4">
              {/* Total Cost Card */}
              <div className="cost-total" data-testid="total-cost-display">
                <p data-pdf-name className="text-base font-heading font-semibold text-white/90 mb-2">
                  {pdfDesignName || "Dhoti Costing Calculator"}
                </p>
                <div className="grid grid-cols-3 gap-3 text-center mb-3">
                  <div>
                    <p className="text-xs text-white/60">Landed Cost</p>
                    <p className="text-xl font-heading font-bold text-white">{result.landed_cost?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60">WSP</p>
                    <p className="text-xl font-heading font-bold text-white">{wspPrice || "—"}</p>
                    {wspPrice && <p className="text-xs text-white/50">MU {wspMarkup}% / MD {wspMarkdown}%</p>}
                  </div>
                  <div>
                    <p className="text-xs text-white/60">MRP</p>
                    <p className="text-xl font-heading font-bold text-white">{mrpPrice || "—"}</p>
                    {mrpPrice && <p className="text-xs text-white/50">MU {mrpMarkup}% / MD {mrpMarkdown}%</p>}
                  </div>
                </div>
                <p className="text-xs text-white/50 text-center">
                  Size: {result.size}m | {result.border_type} | {result.processing_type}
                </p>
              </div>

              {/* Cost Breakdown */}
              <div className="bg-white border border-stone-200 rounded-lg p-5">
                <button
                  data-testid="toggle-details-button"
                  className="flex items-center justify-between w-full text-left"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
                    Cost Breakdown
                  </h3>
                  {showDetails ? <ChevronUp className="h-4 w-4 text-stone-400" /> : <ChevronDown className="h-4 w-4 text-stone-400" />}
                </button>

                {showDetails && (
                  <div className="mt-4 space-y-3">
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Intermediate Values</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-stone-500">Reed Space</span>
                        <span className="text-right font-mono text-stone-900">{result.reed_space}</span>
                        <span className="text-stone-500">Warp Ends</span>
                        <span className="text-right font-mono text-stone-900">{result.warp_ends}</span>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Weights (per mtr)</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-stone-500">Warp Weight</span>
                        <span className="text-right font-mono text-stone-900">{result.warp_weight_per_mtr?.toFixed(6)}</span>
                        <span className="text-stone-500">Weft Weight</span>
                        <span className="text-right font-mono text-stone-900">{result.weft_weight_per_mtr?.toFixed(6)}</span>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Cost Items</p>
                        <div className="flex gap-6 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                          <span className="w-20 text-right">Per Mtr</span>
                          <span className="w-20 text-right">Per Dhoti</span>
                        </div>
                      </div>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-stone-500 flex-1">Warp Raw Cost</span>
                          <span className="font-mono text-stone-900 w-20 text-right">{result.warp_raw_cost?.toFixed(2)}</span>
                          <span className="font-mono text-stone-700 w-20 text-right">{(result.warp_raw_cost * result.size)?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-stone-500 flex-1">Warp Sizing</span>
                          <span className="font-mono text-stone-900 w-20 text-right">{result.warp_sizing_charge?.toFixed(2)}</span>
                          <span className="font-mono text-stone-700 w-20 text-right">{(result.warp_sizing_charge * result.size)?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center font-medium">
                          <span className="text-stone-700 flex-1">Warp Total</span>
                          <span className="font-mono text-stone-900 w-20 text-right">{result.warp_total_cost?.toFixed(2)}</span>
                          <span className="font-mono text-stone-700 w-20 text-right">{(result.warp_total_cost * result.size)?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-stone-500 flex-1">{calcMode === "tissue" ? "Weft 1" : "Weft Cost"}</span>
                          <span className="font-mono text-stone-900 w-20 text-right">{result.weft_cost?.toFixed(2)}</span>
                          <span className="font-mono text-stone-700 w-20 text-right">{calcMode === "tissue" ? (result.weft_cost * result.size / 2)?.toFixed(2) : (result.weft_cost * result.size)?.toFixed(2)}</span>
                        </div>
                        {calcMode === "tissue" && result.weft2_cost > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-stone-500 flex-1">Weft 2 ({result.weft2_count}/{result.weft2_quality})</span>
                            <span className="font-mono text-stone-900 w-20 text-right">{result.weft2_cost?.toFixed(2)}</span>
                            <span className="font-mono text-stone-700 w-20 text-right">{(result.weft2_cost * result.size / 2)?.toFixed(2)}</span>
                          </div>
                        )}
                        {result.has_bobbin1 && (
                          <div className="flex justify-between items-center">
                            <span className="text-stone-500 flex-1">Bobbin - Polyester</span>
                            <span className="font-mono text-stone-900 w-20 text-right">{result.bobbin1_cost?.toFixed(2)}</span>
                            <span className="font-mono text-stone-700 w-20 text-right">{(result.bobbin1_cost * result.size)?.toFixed(2)}</span>
                          </div>
                        )}
                        {result.has_bobbin2 && (
                          <div className="flex justify-between items-center">
                            <span className="text-stone-500 flex-1">Bobbin - Jari</span>
                            <span className="font-mono text-stone-900 w-20 text-right">{result.bobbin2_cost?.toFixed(2)}</span>
                            <span className="font-mono text-stone-700 w-20 text-right">{(result.bobbin2_cost * result.size)?.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center font-medium">
                          <span className="text-stone-700 flex-1">Total Bobbin</span>
                          <span className="font-mono text-stone-900 w-20 text-right">{result.total_bobbin_cost?.toFixed(2)}</span>
                          <span className="font-mono text-stone-700 w-20 text-right">{(result.total_bobbin_cost * result.size)?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-stone-500 flex-1">Kuri ({result.kuri_count}/{result.kuri_quality})</span>
                          <span className="font-mono text-stone-900 w-20 text-right">—</span>
                          <span className="font-mono text-stone-700 w-20 text-right">{result.kuri_cost?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-stone-500 flex-1">Weaving Wages</span>
                          <span className="font-mono text-stone-900 w-20 text-right">{result.weaving_wages?.toFixed(2)}</span>
                          <span className="font-mono text-stone-700 w-20 text-right">{(result.weaving_wages * result.size)?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="flex justify-between items-center text-sm font-medium">
                      <span className="text-stone-700 flex-1">Cost</span>
                      <span className="font-mono text-stone-900 w-20 text-right">{result.cost_per_mtr?.toFixed(2)}</span>
                      <span className="font-mono text-stone-900 w-20 text-right">{result.cost_per_dhoti?.toFixed(2)}</span>
                    </div>

                    <Separator />

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-stone-500 flex-1">Weaver Profit ({result.weaver_profit_pct}%)</span>
                        <span className="w-20"></span>
                        <span className="font-mono text-stone-900 w-20 text-right">{result.profit_amount?.toFixed(2)}</span>
                      </div>
                    </div>

                    <Separator />

                    <div className="flex justify-between items-center text-sm font-medium">
                      <span className="text-stone-700 flex-1">Greige Cost per Dhoti</span>
                      <span className="w-20"></span>
                      <span className="font-mono text-stone-900 w-20 text-right">{result.greige_cost_per_dhoti?.toFixed(2)}</span>
                    </div>

                    <Separator />

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-stone-500 flex-1">Processing ({result.processing_mtr_rate}/mtr x {result.size})</span>
                        <span className="w-20"></span>
                        <span className="font-mono text-stone-900 w-20 text-right">{result.processing_cost?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-stone-500 flex-1">Folding</span>
                        <span className="w-20"></span>
                        <span className="font-mono text-stone-900 w-20 text-right">{result.folding_cost?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-stone-500 flex-1">Trims</span>
                        <span className="w-20"></span>
                        <span className="font-mono text-stone-900 w-20 text-right">{result.trims_cost?.toFixed(2)}</span>
                      </div>
                    </div>

                    <Separator />

                    <div className="flex justify-between items-center text-base font-bold pt-1">
                      <span className="text-stone-900 flex-1">Final Cost per Dhoti</span>
                      <span className="w-20"></span>
                      <span className="font-mono text-stone-900 w-20 text-right">{result.final_cost_per_dhoti?.toFixed(2)}</span>
                    </div>

                    <Separator />

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-stone-500 flex-1">Warehouse Expenses ({result.warehouse_pct}%)</span>
                        <span className="w-20"></span>
                        <span className="font-mono text-stone-900 w-20 text-right">{result.warehouse_cost?.toFixed(2)}</span>
                      </div>
                    </div>

                    <Separator />

                    <div className="flex justify-between items-center text-base font-bold pt-1">
                      <span className="text-[#C2410C] flex-1">Landed Cost per Dhoti</span>
                      <span className="w-20"></span>
                      <span className="font-mono text-[#C2410C] w-20 text-right">{result.landed_cost?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* WSP & MRP Pricing */}
              <div className="bg-white border border-stone-200 rounded-lg p-5">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-4">
                  WSP & MRP Pricing
                </h3>
                <div className="space-y-5">
                  {/* WSP from Landed Cost */}
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-2 block">WSP (from Landed Cost: {result.landed_cost?.toFixed(2)})</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <span className="text-xs text-stone-400">Markup %</span>
                        <Input data-testid="wsp-markup-input" type="number" step="0.01" placeholder="e.g. 50"
                          value={wspMarkup}
                          onChange={(e) => {
                            const v = e.target.value; setWspMarkup(v); setWspMarkdown("");
                            if (v && result?.landed_cost) {
                              const p = Math.round(result.landed_cost * (1 + parseFloat(v) / 100));
                              setWspPrice(p); setWspMarkdown((((p - result.landed_cost) / p) * 100).toFixed(2));
                              setMrpMarkup(""); setMrpMarkdown(""); setMrpPrice("");
                            } else { setWspPrice(""); }
                          }}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">Markdown %</span>
                        <Input data-testid="wsp-markdown-input" type="number" step="0.01" placeholder="e.g. 33"
                          value={wspMarkdown}
                          onChange={(e) => {
                            const v = e.target.value; setWspMarkdown(v); setWspMarkup("");
                            if (v && result?.landed_cost) {
                              const md = parseFloat(v);
                              if (md < 100) {
                                const p = Math.round(result.landed_cost / (1 - md / 100));
                                setWspPrice(p); setWspMarkup((((p - result.landed_cost) / result.landed_cost) * 100).toFixed(2));
                                setMrpMarkup(""); setMrpMarkdown(""); setMrpPrice("");
                              }
                            } else { setWspPrice(""); }
                          }}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">WSP Price</span>
                        <Input data-testid="wsp-price-input" type="number" step="1" placeholder="Price"
                          value={wspPrice}
                          onChange={(e) => {
                            const p = e.target.value; setWspPrice(p);
                            if (p && result?.landed_cost) {
                              const price = parseFloat(p);
                              if (price > 0 && result.landed_cost > 0) {
                                setWspMarkup((((price - result.landed_cost) / result.landed_cost) * 100).toFixed(2));
                                setWspMarkdown((((price - result.landed_cost) / price) * 100).toFixed(2));
                                setMrpMarkup(""); setMrpMarkdown(""); setMrpPrice("");
                              }
                            } else { setWspMarkup(""); setWspMarkdown(""); }
                          }}
                          className="h-9 mt-1 font-semibold"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* MRP from WSP */}
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-2 block">MRP (from WSP: {wspPrice || "—"})</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <span className="text-xs text-stone-400">Markup %</span>
                        <Input data-testid="mrp-markup-input" type="number" step="0.01" placeholder="e.g. 40"
                          value={mrpMarkup}
                          onChange={(e) => {
                            const v = e.target.value; setMrpMarkup(v); setMrpMarkdown("");
                            const wsp = parseFloat(wspPrice);
                            if (v && wsp > 0) {
                              const p = Math.round(wsp * (1 + parseFloat(v) / 100) / 5) * 5;
                              setMrpPrice(p); setMrpMarkdown((((p - wsp) / p) * 100).toFixed(2));
                            } else { setMrpPrice(""); }
                          }}
                          className="h-9 mt-1"
                          disabled={!wspPrice}
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">Markdown %</span>
                        <Input data-testid="mrp-markdown-input" type="number" step="0.01" placeholder="e.g. 28"
                          value={mrpMarkdown}
                          onChange={(e) => {
                            const v = e.target.value; setMrpMarkdown(v); setMrpMarkup("");
                            const wsp = parseFloat(wspPrice);
                            if (v && wsp > 0) {
                              const md = parseFloat(v);
                              if (md < 100) {
                                const p = Math.round(wsp / (1 - md / 100) / 5) * 5;
                                setMrpPrice(p); setMrpMarkup((((p - wsp) / wsp) * 100).toFixed(2));
                              }
                            } else { setMrpPrice(""); }
                          }}
                          className="h-9 mt-1"
                          disabled={!wspPrice}
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">MRP Price</span>
                        <Input data-testid="mrp-price-input" type="number" step="5" placeholder="Price"
                          value={mrpPrice}
                          onChange={(e) => {
                            const p = e.target.value; setMrpPrice(p);
                            const wsp = parseFloat(wspPrice);
                            if (p && wsp > 0) {
                              const price = parseFloat(p);
                              if (price > 0) {
                                setMrpMarkup((((price - wsp) / wsp) * 100).toFixed(2));
                                setMrpMarkdown((((price - wsp) / price) * 100).toFixed(2));
                              }
                            } else { setMrpMarkup(""); setMrpMarkdown(""); }
                          }}
                          className="h-9 mt-1 font-semibold"
                          disabled={!wspPrice}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Reverse: WSP from MRP */}
                  <div>
                    <Label className="text-sm font-medium text-stone-700 mb-2 block">Calculate WSP from MRP</Label>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div>
                        <span className="text-xs text-stone-400">MRP</span>
                        <Input data-testid="rev-mrp-input" type="number" step="1" placeholder="Enter MRP"
                          value={revMrpInput}
                          onChange={(e) => {
                            setRevMrpInput(e.target.value);
                            setRevWspResult(""); setRevWspMarkup(""); setRevWspMarkdown("");
                          }}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">Markup %</span>
                        <Input data-testid="rev-wsp-markup-input" type="number" step="0.01" placeholder="e.g. 40"
                          value={revWspMarkup}
                          onChange={(e) => {
                            const v = e.target.value; setRevWspMarkup(v); setRevWspMarkdown("");
                            const mrp = parseFloat(revMrpInput);
                            if (v && mrp > 0) {
                              const mu = parseFloat(v);
                              const w = Math.round(mrp / (1 + mu / 100));
                              setRevWspResult(w); setRevWspMarkdown((((mrp - w) / mrp) * 100).toFixed(2));
                            } else { setRevWspResult(""); }
                          }}
                          className="h-9 mt-1"
                          disabled={!revMrpInput}
                        />
                      </div>
                      <div>
                        <span className="text-xs text-stone-400">Markdown %</span>
                        <Input data-testid="rev-wsp-markdown-input" type="number" step="0.01" placeholder="e.g. 28"
                          value={revWspMarkdown}
                          onChange={(e) => {
                            const v = e.target.value; setRevWspMarkdown(v); setRevWspMarkup("");
                            const mrp = parseFloat(revMrpInput);
                            if (v && mrp > 0) {
                              const md = parseFloat(v);
                              const w = Math.round(mrp * (1 - md / 100));
                              setRevWspResult(w); setRevWspMarkup((((mrp - w) / w) * 100).toFixed(2));
                            } else { setRevWspResult(""); }
                          }}
                          className="h-9 mt-1"
                          disabled={!revMrpInput}
                        />
                      </div>
                    </div>
                    {revWspResult && (
                      <div className="bg-stone-50 rounded-md p-3 text-center">
                        <span className="text-xs text-stone-500">Calculated WSP: </span>
                        <span className="font-heading font-bold text-lg text-[#C2410C]">{revWspResult}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Button
                data-testid="export-pdf-button"
                variant="outline"
                className="w-full border-stone-300 text-stone-700 hover:bg-stone-100"
                onClick={handleExportPdf}
              >
                <FileDown className="h-4 w-4 mr-2" />
                Export to PDF
              </Button>
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-lg p-8 text-center hidden lg:block">
              <Calculator className="h-12 w-12 mx-auto text-stone-300 mb-3" strokeWidth={1} />
              <p className="text-sm text-stone-400">
                Fill in the specifications and click calculate to see the cost breakdown.
              </p>
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {/* Mobile sticky total */}
      {calcMode === "regular" && result && (
        <div className="sticky-total md:hidden p-4 flex justify-between items-center">
          <div>
            <p className="text-xs text-stone-500">Landed Cost per Dhoti</p>
            <p className="text-xl font-heading font-bold text-[#C2410C]">{result.landed_cost?.toFixed(2)}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            data-testid="mobile-toggle-details"
            onClick={() => {
              setShowDetails(!showDetails);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            View Details
          </Button>
        </div>
      )}
    </div>

    {/* Save Design Dialog */}
    <Dialog open={saveOpen} onOpenChange={(o) => { if (!o) { setTagStepId(null); setPdfStep(false); } setSaveOpen(o); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">{pdfStep ? "Export PDF?" : tagStepId ? "Tag Barcode (Optional)" : loadedDesignId ? "Update Design" : "Save Design"}</DialogTitle>
          <DialogDescription>{pdfStep ? "" : tagStepId ? "" : loadedDesignId ? "Update the existing design with current changes." : "Give this design a name and optional description."}</DialogDescription>
        </DialogHeader>
        {pdfStep ? (
          <PdfPromptStep
            onExport={() => {
              setPdfStep(false); setSaveOpen(false);
              setTimeout(() => {
                document.querySelector('[data-testid="export-pdf-button"]')?.click();
                setTimeout(() => resetRegular(), 300);
              }, 60);
            }}
            onSkip={() => { setPdfStep(false); setSaveOpen(false); resetRegular(); }}
          />
        ) : tagStepId ? (
          <TagBarcodePicker
            designId={tagStepId}
            onTagged={async () => { const id = tagStepId; setTagStepId(null); await refreshAfterTag(id, calcMode); setPdfStep(true); }}
            onSkip={() => { setTagStepId(null); setPdfStep(true); }}
          />
        ) : (
          <>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Design Name *</Label>
                <Input
                  data-testid="design-name-input"
                  placeholder="e.g. Premium Dhoti 8m"
                  value={designName}
                  onChange={(e) => setDesignName(e.target.value)}
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Description</Label>
                <Input
                  data-testid="design-desc-input"
                  placeholder="Optional notes..."
                  value={designDesc}
                  onChange={(e) => setDesignDesc(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
              <Button
                data-testid="design-submit-button"
                className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
                onClick={handleSaveDesign}
                disabled={saving}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {saving ? "Saving..." : loadedDesignId ? "Update Design" : "Save Design"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* PDF Export Name Dialog */}
    <Dialog open={pdfNameOpen} onOpenChange={setPdfNameOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">Export to PDF</DialogTitle>
          <DialogDescription>Enter a design name for the PDF header.</DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Label className="text-sm font-medium text-stone-700 mb-1.5 block">Design Name</Label>
          <Input
            data-testid="pdf-name-input"
            placeholder="e.g. Premium Dhoti 8m"
            value={pdfDesignName}
            onChange={(e) => setPdfDesignName(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") executePdfExport(); }}
          />
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => setPdfNameOpen(false)}>Cancel</Button>
          <Button
            data-testid="pdf-export-confirm"
            className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
            onClick={executePdfExport}
          >
            <FileDown className="h-4 w-4 mr-2" />
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
}
